import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldAlert } from "lucide-react";

/**
 * Public /set-password page — activated by the reset-token link in the
 * welcome / password-reset email. Validates the token first (shows the
 * customer's name so they know they've landed on the right account),
 * then lets them choose a new password and pushes them into the login
 * page.
 */
export default function SetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [state, setState] = useState({ loading: true, valid: false, error: null, email: "", name: "" });
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        if (!token) {
            setState({ loading: false, valid: false, error: "Missing token", email: "", name: "" });
            return;
        }
        fetch(`${API}/auth/password-reset/validate/${encodeURIComponent(token)}`)
            .then(async r => {
                if (!r.ok) {
                    const body = await r.json().catch(() => ({}));
                    throw new Error(body.detail || `Link invalid (${r.status})`);
                }
                return r.json();
            })
            .then(d => setState({ loading: false, valid: true, error: null, email: d.email, name: d.name || "" }))
            .catch(e => setState({ loading: false, valid: false, error: e.message, email: "", name: "" }));
    }, [token]);

    const passwordsMatch = pw && pw === pw2;
    const strongEnough = pw.length >= 8;
    const canSubmit = passwordsMatch && strongEnough && !submitting;

    async function submit(e) {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const r = await fetch(`${API}/auth/password-reset/confirm`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, new_password: pw }),
            });
            if (!r.ok) {
                const body = await r.json().catch(() => ({}));
                throw new Error(body.detail || "Failed to set password");
            }
            toast.success("Password set. Please sign in with your new password.");
            setTimeout(() => nav("/login", { replace: true }), 900);
        } catch (e) {
            toast.error(e.message);
        } finally { setSubmitting(false); }
    }

    return (
        <div className="min-h-screen bg-[#0B0D11] flex items-center justify-center p-6" data-testid="set-password-page">
            <div className="w-full max-w-md bg-white rounded-md shadow-xl overflow-hidden">
                <div className="bg-[#0B0D11] px-8 py-6">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0A0A0A]">RPPL Solar</div>
                    <div className="text-lg font-semibold text-white mt-1">Set your password</div>
                </div>
                <div className="p-8">
                    {state.loading ? (
                        <div className="flex items-center gap-2 text-sm text-zinc-500">
                            <Loader2 className="w-4 h-4 animate-spin" /> Validating your invite link…
                        </div>
                    ) : !state.valid ? (
                        <div className="flex flex-col gap-4 items-start" data-testid="set-password-invalid">
                            <div className="flex items-start gap-2 p-3 rounded-sm bg-red-50 border border-red-200 text-red-700 text-sm">
                                <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-semibold">Link no longer valid</div>
                                    <div className="text-xs mt-0.5">{state.error}</div>
                                </div>
                            </div>
                            <Link to="/forgot-password" className="text-xs text-[#0A0A0A] hover:underline" data-testid="request-new-link">
                                Request a new password reset link →
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <div className="p-3 rounded-sm bg-green-50 border border-green-200 text-green-700 text-xs flex items-center gap-2" data-testid="set-password-account">
                                <ShieldCheck className="w-4 h-4" />
                                <div>
                                    Setting password for <b>{state.name || state.email}</b><br />
                                    <span className="font-mono text-[10px]">{state.email}</span>
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">New password</label>
                                <Input
                                    type="password"
                                    value={pw}
                                    onChange={e => setPw(e.target.value)}
                                    required
                                    minLength={8}
                                    autoFocus
                                    className="rounded-sm"
                                    data-testid="new-password"
                                />
                                <div className="text-[10px] text-zinc-500 mt-1">Minimum 8 characters.</div>
                            </div>
                            <div>
                                <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Confirm password</label>
                                <Input
                                    type="password"
                                    value={pw2}
                                    onChange={e => setPw2(e.target.value)}
                                    required
                                    minLength={8}
                                    className="rounded-sm"
                                    data-testid="confirm-password"
                                />
                                {pw2 && !passwordsMatch && <div className="text-[11px] text-red-600 mt-1">Passwords don't match.</div>}
                            </div>
                            <Button type="submit" disabled={!canSubmit} className="w-full bg-[#0A0A0A] hover:bg-black text-white rounded-full h-10" data-testid="set-password-submit">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set password & continue"}
                            </Button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
